import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Mail, Lock, ArrowRight, HardHat } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";

const Login = ({ onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post("/auth/login", { email, password });
      toast.success(t('messages.operationSuccessful'), { position: 'bottom-right' });
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        {/* Technical grid overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        />
        
        {/* Blueprint accent lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500" />
        <div className="absolute bottom-0 left-0 w-1 h-full bg-indigo-500/30" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-600 rounded-sm flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white tracking-tight uppercase">SitePulse</h1>
              <p className="text-xs text-slate-400 uppercase tracking-widest">Construction Management</p>
            </div>
          </div>
          
          {/* Hero Text */}
          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-indigo-400 uppercase tracking-widest">Enterprise Platform</p>
              <h2 className="font-display text-5xl font-bold text-white leading-tight tracking-tight uppercase">
                Build with<br />Precision
              </h2>
            </div>
            <p className="text-slate-400 text-base max-w-md leading-relaxed">
              Streamline your construction projects with real-time tracking, financial oversight, and team coordination — all in one powerful platform.
            </p>
            
            {/* Feature badges */}
            <div className="flex flex-wrap gap-2 pt-4">
              {['Project Tracking', 'Financial Control', 'Team Management'].map((feature) => (
                <span 
                  key={feature}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-sm text-xs font-medium text-slate-300 uppercase tracking-wider"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <HardHat className="w-4 h-4" />
            <span className="uppercase tracking-wider">Trusted by construction professionals</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col bg-slate-50">
        {/* Top bar */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-8 h-8 bg-indigo-600 rounded-sm flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-slate-900 uppercase tracking-tight">SitePulse</span>
          </div>
          <div className="lg:ml-auto">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="mb-8">
              <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">
                {mode === "forgot" ? t('auth.reset.forgotTitle') : t('auth.welcomeBack')}
              </h2>
              <p className="text-slate-500 mt-2 text-sm">
                {mode === "forgot" ? t('auth.reset.forgotSubtitle') : t('auth.signInToContinue')}
              </p>
            </div>

            {/* Form */}
            {mode === "forgot" ? <ForgotPasswordForm onBack={() => setMode("login")} /> : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auth.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-12 bg-white border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 h-12 bg-white border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>
                <div className="flex justify-end">
                  <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
                    {t('auth.reset.forgotLink')}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs uppercase tracking-widest rounded-sm transition-all duration-150 active:scale-[0.98]"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('auth.signingIn')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t('auth.login')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="p-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400 uppercase tracking-wider">
            Secure Construction Management Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
